import {useVirtualizer} from '@tanstack/react-virtual'
import clsx from 'clsx'
import {isSelectiveAiDomains} from 'common/HelperFunctions'
import {CUSTOM_DESELECT_ALL_VALUE, CUSTOM_SELECT_ALL_VALUE} from 'common/constants'
import React, {FC, useEffect, useRef} from 'react'
import {ValueContainerProps, components} from 'react-select'

const RSValueContainer: any = components.ValueContainer as any
const RSOption: any = components.Option as any
const RSMenuList: any = components.MenuList as any

/**
 * Single-select on Selective-AI (Da1TimeTable) tenants: a checkbox reads as "you can tick
 * several", which these fields can't do, so their rows drop the checkbox. Multi-select keeps
 * checkboxes everywhere, and the other portals keep the checkbox rows they have always had.
 * Shared by every react-select `Option` in the app (BaseSelect, CustomAsyncSelect,
 * CustomTagSelect, onboarding GenericTable) so the rule is applied in one place.
 */
export const isPlainSingleSelect = (props: any): boolean =>
  !props.isMulti && !props.selectProps?.isMulti && isSelectiveAiDomains()

/** Plain single-choice row — label, with a check mark on the current choice. */
export const SingleChoiceOption: FC<any> = (props) => (
  <RSOption {...props}>
    <span className='d-flex align-items-center gap-2'>
      <span className='flex-grow-1'>{props.children ?? props.label}</span>
      {props.isSelected && <i className='bi bi-check-lg text-primary' aria-hidden='true' />}
    </span>
  </RSOption>
)

export const ValueContainer: FC<ValueContainerProps> = (props) => {
  const {children, isMulti, getValue} = props

  if (!isMulti) {
    return <RSValueContainer {...(props as any)} />
  }

  const values = getValue()
  let valueLabel = ''

  if (values.length > 0) {
    // getOptionLabel can hand back null (options built straight from API rows
    // with a null label column), so normalize before measuring/slicing.
    const rawLabel = props.selectProps.getOptionLabel(values[0])
    let label = rawLabel === null || rawLabel === undefined ? '' : String(rawLabel)
    if (label.length > 30) {
      label = label.substring(0, 30) + '...'
    }
    valueLabel += label
  }
  if (values.length > 1) valueLabel += ` & ${values.length - 1} more`

  const childrenToRender = React.Children.toArray(children).filter((child: any) => {
    if (!React.isValidElement(child)) return false

    const isReactSelectInput = child.type === components.Input
    const isReactSelectPlaceholder = child.type === components.Placeholder

    return isReactSelectInput || isReactSelectPlaceholder
  })

  return (
    <RSValueContainer {...(props as any)}>
      {!props.selectProps.inputValue && valueLabel}
      {childrenToRender}
    </RSValueContainer>
  )
}

export const Option = (props: any) => {
  const isSelectAllRow = [CUSTOM_SELECT_ALL_VALUE, CUSTOM_DESELECT_ALL_VALUE].includes(props.value)
  // The injected select-all row carries an explicit flag so the deselect
  // styling doesn't depend on the translated label containing the English
  // word "deselect" (which it doesn't in ur/ar/tr/fr locales). The label
  // check remains as a fallback for caller-supplied select-all options.
  const isDeselectAll =
    props.data?.isDeselectAll === true ||
    props.value === CUSTOM_DESELECT_ALL_VALUE ||
    (typeof props.label === 'string' && props.label.toLowerCase().includes('deselect'))

  if (isSelectAllRow) {
    return (
      <RSOption {...props}>
        <div
          className={clsx(
            'form-check form-check-sm form-check-solid',
            isDeselectAll ? 'text-danger' : 'text-primary'
          )}
        >
          <input className='form-check-input' type='checkbox' checked={isDeselectAll} readOnly />
          <label
            className={clsx(
              'form-check-label fw-bold',
              isDeselectAll ? 'text-danger' : 'text-primary'
            )}
          >
            {props.label}
          </label>
        </div>
      </RSOption>
    )
  }

  if (isPlainSingleSelect(props)) return <SingleChoiceOption {...props} />

  return (
    <RSOption {...props}>
      <div className='form-check form-check-sm form-check-solid'>
        <input
          name={props.value || props.data?.value}
          className='form-check-input'
          type='checkbox'
          checked={props.isSelected}
          readOnly
          aria-label={props.label}
        />
        <label className='form-check-label'>{props.children ?? props.label}</label>
      </div>
    </RSOption>
  )
}

/** Menus below this size render the stock react-select list. */
const VIRTUALIZE_FROM = 50
/** Estimate for one checkbox option row; measureElement corrects real heights. */
const OPTION_ROW_ESTIMATE = 38

const VirtualMenuRows = ({
  items,
  maxHeight,
  innerRef,
  focusedIndex,
}: {
  items: React.ReactNode[]
  maxHeight: number
  innerRef: any
  focusedIndex: number
}) => {
  // React Compiler must not memoize this component: the virtualizer instance is
  // stable across renders and mutates internally on scroll, so compiler-cached
  // getVirtualItems()/getTotalSize() results would freeze the rendered window
  // (rows stop updating while scrolling). Known TanStack Virtual v3 limitation.
  'use no memo'

  const parentRef = useRef<HTMLDivElement | null>(null)
  const setScrollRef = (el: HTMLDivElement | null) => {
    parentRef.current = el
    if (typeof innerRef === 'function') innerRef(el)
  }

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => OPTION_ROW_ESTIMATE,
    overscan: 10,
    // Lets the first window render before the ResizeObserver delivers a real
    // rect (also what makes this work in jsdom tests).
    initialRect: {width: 300, height: maxHeight || 300},
    measureElement: (el) => el.getBoundingClientRect().height || OPTION_ROW_ESTIMATE,
  })

  // Offscreen options have no DOM node, so react-select's own
  // scroll-focused-option-into-view cannot work; follow keyboard focus here.
  useEffect(() => {
    if (focusedIndex >= 0) virtualizer.scrollToIndex(focusedIndex)
  }, [focusedIndex, virtualizer])

  return (
    <div
      ref={setScrollRef}
      className='react-select__menu-list'
      style={{maxHeight, overflowY: 'auto', position: 'relative'}}
    >
      <div style={{height: virtualizer.getTotalSize(), position: 'relative', width: '100%'}}>
        {virtualizer.getVirtualItems().map((virtualItem) => (
          <div
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            {items[virtualItem.index]}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Virtualizes the option list for large menus so a select with thousands of
 * options doesn't mount thousands of DOM nodes (which froze the page before).
 * Small menus keep the stock MenuList and all of its built-in behavior.
 */
export const MenuList = (props: any) => {
  const items = React.Children.toArray(props.children)
  if (items.length < VIRTUALIZE_FROM) {
    return <RSMenuList {...props} />
  }
  const focusedIndex = items.findIndex((child: any) => child?.props?.isFocused)
  return (
    <VirtualMenuRows
      items={items}
      maxHeight={props.maxHeight}
      innerRef={props.innerRef}
      focusedIndex={focusedIndex}
    />
  )
}

// Hoisted so their identity never changes; together with stable handler
// identities this lets the memoized Select skip re-rendering when unrelated
// form fields change (Formik context re-renders every field on each keystroke).
export const SELECT_COMPONENTS = {Option, ValueContainer, MenuList}

export const SELECT_CLASSNAMES = {
  container: () => 'react-select-container',
  control: () => 'react-select__control',
  menu: () => 'react-select__menu',
  option: (props: any) =>
    `react-select__option ${props.isSelected ? `react-select__option-selected` : ''}`,
  indicatorSeparator: () => 'react-select__indicator-separator',
  placeholder: () => 'react-select__placeholder',
  input: () => 'react-select__input',
  singleValue: () => 'react-select__single-value',
}
